import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  SimpleGrid,
  GridItem,
  Select,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import Translate from "../../../components/LanguageTranslation/Translate";
import currencyFormatter from "currency-formatter";

import axiosInstance from "../../../helpers/axiosInstance";
import { useCurrency } from "../../CurrencyExchange/CurrencyContext";

const AddRV = ({ isOpen, onClose, addTripItem, editItem = null, onEditSuccess = null }) => {
  const toast = useToast();
  const { currency } = useCurrency();

  const [rates, setRates] = useState(null);

  const [title, setTitle] = useState("");
  const [bookingUrl, setBookingUrl] = useState("");
  const [vehicleName, setVehicleName] = useState("");
  const [pricePerDay, setPricePerDay] = useState("");
  const [rvCurrency, setRVCurrency] = useState(currency);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const DEFAULT_THUMBNAIL = "";

  const availableCurrencies = currencyFormatter.currencies.map((currency) => ({
    code: currency.code,
    label: `${currency.code} - ${currency.symbol || ""}`,
  }));

  const normalizedURL = (url) => {
    return url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;
  };

  const isValidURL = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const fetchRates = async () => {
    try {
      const response = await axios.get(
        "https://api.exchangerate-api.com/v4/latest/USD"
      );
      if (response.data && response.data.rates) {
        const fetchedRates = response.data.rates;
        setRates({ ...fetchedRates, USD: 1 });
      } else {
        throw new Error("Invalid rates data");
      }
    } catch (error) {
      toast({
        title: "Error fetching exchange rates",
        description: "Using default conversion rates",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      setRates({ USD: 1, EUR: 0.85, GBP: 0.75 });
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  // Reset form whenever modal opens
  useEffect(() => {
    if (isOpen) {
      if (editItem) {
        // Populate form with existing data for editing
        setTitle(editItem.title || "");
        setBookingUrl(editItem.booking_url || "");
        setVehicleName(editItem.vehicle_name || "");
        setPricePerDay(editItem.price_per_day?.toString() || "");
        setRVCurrency(editItem.currency || currency);
      } else {
        // Reset form for new RV
        setTitle("");
        setBookingUrl("");
        setVehicleName("");
        setPricePerDay("");
        setRVCurrency(currency);
      }
      setErrors({});
    }
  }, [isOpen, editItem, currency]);

  const convertCurrency = (amount, currency) => {
    if (!rates || !rates[currency]) {
      toast({
        title: "Conversion error",
        description: `Unable to convert ${currency}, using USD as fallback`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return amount;
    }
    return (amount / rates[currency]).toFixed(2);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = "Title is required.";
    if (!bookingUrl.trim() || !isValidURL(normalizedURL(bookingUrl)))
      newErrors.bookingUrl =
        "Please enter a valid URL, e.g. https://example.com";
    if (!vehicleName.trim())
      newErrors.vehicleName = "Vehicle name is required.";
    if (!pricePerDay || isNaN(pricePerDay) || pricePerDay <= 0)
      newErrors.pricePerDay = "Please enter a valid price per day.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const convertedPrice = convertCurrency(pricePerDay, rvCurrency);

    const payload = {
      title,
      link: normalizedURL(bookingUrl),
      vehicle_name: vehicleName,
      price_per_day: convertedPrice,
      currency: rvCurrency,
    };

    // Add item_id to payload if editing
    if (editItem && editItem._id) {
      payload.item_id = editItem._id;
    }

    try {
      const response = await axiosInstance.post(
        "/bucket-list/add-manual-rv",
        payload
      );

      if (response.data.success) {
        if (editItem && editItem._id) {
          // Update existing item
          const updatedItem = {
            ...editItem,
            title,
            booking_url: normalizedURL(bookingUrl),
            vehicle_name: vehicleName,
            price_per_day: Number(convertedPrice),
            currency: rvCurrency,
          };
          if (onEditSuccess) {
            onEditSuccess(updatedItem);
          }
        } else {
          // Add new item
          const rvObj = {
            _id: response.data.data._id,
            vehicle_name: vehicleName,
            booking_url: normalizedURL(bookingUrl),
            price_per_day: Number(convertedPrice),
            type: "rv",
            title,
            currency: rvCurrency,
            image_url: DEFAULT_THUMBNAIL,
            preference: "rv",
            data_saved: "manual",
            category: "transportation",
          };
          // Add 'data' property as expected by your cards
          rvObj.data = { ...rvObj };
          addTripItem(rvObj);
        }
        
        toast({
          title: editItem ? "RV updated" : "RV added",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(response.data.message || "Failed to save RV");
      }
    } catch (error) {
      toast({
        title: "Error saving RV",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Translate text={editItem ? "Edit RV" : "Add RVs"} />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <SimpleGrid spacing={4} columns={2}>
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.title}>
                <FormLabel>
                  {" "}
                  <Translate text="Title" />
                </FormLabel>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.title} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.bookingUrl}>
                <FormLabel>
                  {" "}
                  <Translate text="Booking Link" />
                </FormLabel>
                <Input
                  value={bookingUrl}
                  onChange={(e) => setBookingUrl(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.bookingUrl} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl isInvalid={errors.vehicleName}>
                <FormLabel>
                  {" "}
                  <Translate text="Vehicle Name" />
                </FormLabel>
                <Input
                  value={vehicleName}
                  onChange={(e) => setVehicleName(e.target.value)}
                />
                <FormErrorMessage>
                  {" "}
                  <Translate text={errors.vehicleName} />
                </FormErrorMessage>
              </FormControl>
            </GridItem>
            <FormControl isInvalid={errors.pricePerDay}>
              <FormLabel>
                {" "}
                <Translate text="Price per Day" />
              </FormLabel>
              <Input
                type="number"
                value={pricePerDay}
                onChange={(e) => setPricePerDay(e.target.value)}
              />
              <FormErrorMessage>
                {" "}
                <Translate text={errors.pricePerDay} />
              </FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel>
                {" "}
                <Translate text="Currency" />
              </FormLabel>
              <Select
                value={rvCurrency}
                onChange={(e) => setRVCurrency(e.target.value)}
              >
                {availableCurrencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    <Translate text={c.label} />
                  </option>
                ))}
              </Select>
            </FormControl>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="customGreen"
            mr={3}
            onClick={handleCreate}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            <Translate text="Save" />
          </Button>
          <Button onClick={onClose} isDisabled={isSubmitting}>
            <Translate text="Cancel" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddRV;
